import React, { useEffect, useRef, useState } from "react";
import {
  IconBuilding, IconCheck, IconExternal, IconImage, IconKey, IconRefresh, IconShield, IconTrash, IconUpload, IconUser, IconWarning,
} from "../components/ui/icons";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import { Skeleton } from "../components/ui/Skeleton";
import { ErrorState } from "../components/ui/StateBlock";
import { apiFetch } from "../utils/api";
import { invalidateBranding, imageUrl } from "../utils/branding";
import { fileToResizedDataUrl } from "../utils/image";
import { LICENSE_TONE_CLASS, licenseInfo } from "../utils/license";
import { notifyProfileChanged, useProfile } from "../hooks/useProfile";

const FIELD_LABELS = [
  ["Name", "Naziv"],
  ["Address", "Adresa"],
  ["Zip", "Poštanski broj"],
  ["City", "Grad"],
  ["Telephone", "Telefon"],
  ["BankAccount", "Žiro račun"],
  ["EMail", "Email"],
  ["WebPage", "Web stranica"],
  ["Code", "PIB"],
  ["Number", "MB"],
];

async function postJson(path, body) {
  try {
    const res = await apiFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return await res.json();
  } catch (e) {
    return { success: false, error: "Server nije odgovorio. Proveri vezu i pokušaj ponovo." };
  }
}

function Message({ msg }) {
  if (!msg) return <span role="status" aria-live="polite" className="sr-only" />;
  return (
    <span
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-1.5 text-sm ${
        msg.ok ? "text-success-dark dark:text-success-light" : "text-danger-dark dark:text-danger-light"
      }`}
    >
      {msg.ok ? <IconCheck aria-hidden="true" /> : <IconWarning aria-hidden="true" />}
      {msg.text}
    </span>
  );
}

/** A button that opens the image file picker, downsizes the image and sends it. */
function ImagePicker({ label, maxSide, onPick, disabled, icon: Icon = IconUpload }) {
  const ref = useRef(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          try {
            onPick(await fileToResizedDataUrl(file, maxSide));
          } catch (err) {
            onPick(null, err.message);
          }
        }}
      />
      <Button variant="secondary" size="sm" icon={Icon} disabled={disabled} onClick={() => ref.current?.click()}>
        {label}
      </Button>
    </>
  );
}

export default function Profile() {
  const { user, companies, loading, error, reload } = useProfile();

  if (loading && !user) return <ProfileSkeleton />;
  if (error && !user) return <ErrorState message={String(error)} onRetry={reload} />;
  if (!user) return <ProfileSkeleton />;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <PersonalCard user={user} company={companies.find((f) => f.current) || companies[0]} />
      <PasswordCard />
      <LicenseCard company={companies.find((f) => f.current) || companies[0]} />
      <ActiveFirmCard companies={companies} />
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4" role="status" aria-label="Učitavanje">
      <Skeleton className="h-64 w-full rounded-xl" />
      <Skeleton className="h-20 w-full rounded-xl" />
      <Skeleton className="h-56 w-full rounded-xl" />
    </div>
  );
}

function Avatar({ user, size = "h-20 w-20", text = "text-2xl" }) {
  const initial = (user.firstname || user.username || "?").charAt(0).toUpperCase();
  return user.has_avatar ? (
    <img src={imageUrl("avatar", null, user.avatar_v)} alt="" className={`${size} shrink-0 rounded-xl border border-slate-200 object-cover dark:border-slate-800`} />
  ) : (
    <span aria-hidden="true" className={`flex ${size} shrink-0 items-center justify-center rounded-xl bg-primary/10 ${text} font-semibold text-primary dark:bg-primary/20 dark:text-primary-light`}>
      {initial}
    </span>
  );
}

function PersonalCard({ user, company }) {
  const [form, setForm] = useState({ firstname: user.firstname || "", lastname: user.lastname || "", email: user.email || "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [imgBusy, setImgBusy] = useState(false);
  const [imgMsg, setImgMsg] = useState(null);

  useEffect(() => {
    setForm({ firstname: user.firstname || "", lastname: user.lastname || "", email: user.email || "" });
  }, [user.firstname, user.lastname, user.email]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    const r = await postJson("/profile_save.php", form);
    setSaving(false);
    if (r.success) {
      setMsg({ ok: true, text: "Sačuvano." });
      notifyProfileChanged();
    } else setMsg({ ok: false, text: r.error || "Čuvanje nije uspelo" });
  };

  const sendAvatar = async (dataUrl, err) => {
    if (!dataUrl) return setImgMsg({ ok: false, text: err || "Slika nije učitana" });
    setImgBusy(true);
    const r = await postJson("/image_upload.php?type=avatar", { data: dataUrl });
    setImgBusy(false);
    if (r.success) {
      setImgMsg({ ok: true, text: "Slika je sačuvana." });
      notifyProfileChanged();
    } else setImgMsg({ ok: false, text: r.error });
  };

  const removeAvatar = async () => {
    setImgBusy(true);
    const r = await postJson("/image_upload.php?type=avatar", { remove: true });
    setImgBusy(false);
    if (r.success) {
      setImgMsg(null);
      notifyProfileChanged();
    } else setImgMsg({ ok: false, text: r.error });
  };

  return (
    <Card title="Lični podaci" icon={IconUser}>
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-4">
          <Avatar user={user} />
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              <ImagePicker label={user.has_avatar ? "Zameni sliku" : "Dodaj sliku"} maxSide={256} onPick={sendAvatar} disabled={imgBusy} />
              {user.has_avatar && (
                <>
                  <Button variant="ghost" size="sm" icon={IconTrash} onClick={removeAvatar} disabled={imgBusy}>
                    Ukloni
                  </Button>
                  <a
                    href={imageUrl("avatar", null, user.avatar_v)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-primary hover:underline dark:text-primary-light"
                  >
                    <IconExternal aria-hidden="true" /> Otvori sliku
                  </a>
                </>
              )}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">PNG, JPEG ili WebP. Slika se čuva na serveru uz tvoj nalog.</p>
            <Message msg={imgMsg} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Ime" value={form.firstname} onChange={set("firstname")} maxLength={50} autoComplete="given-name" />
          <Input label="Prezime" value={form.lastname} onChange={set("lastname")} maxLength={50} autoComplete="family-name" />
          <Input label="Korisničko ime" value={user.username || ""} disabled readOnly />
          <Input label="Email" type="email" value={form.email} onChange={set("email")} maxLength={150} autoComplete="email" placeholder="ime@firma.rs" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={save} disabled={saving}>
            {saving ? "Čuvanje..." : "Sačuvaj"}
          </Button>
          <Message msg={msg} />
        </div>
      </div>
    </Card>
  );
}

function PasswordCard() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", repeat: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const close = () => {
    setOpen(false);
    setForm({ current: "", next: "", repeat: "" });
    setErr("");
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.next.length < 8) return setErr("Nova lozinka mora imati najmanje 8 karaktera.");
    if (form.next !== form.repeat) return setErr("Nova lozinka i ponovljena lozinka se ne poklapaju.");
    setBusy(true);
    const r = await postJson("/password_change.php", { current: form.current, new: form.next });
    setBusy(false);
    if (r.success) {
      close();
      setDone(true);
    } else setErr(r.error || "Promena lozinke nije uspela");
  };

  return (
    <Card title="Lozinka" icon={IconKey}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600 dark:text-slate-300">Lozinku menjaš unosom trenutne lozinke.</p>
        <div className="flex items-center gap-3">
          {done && (
            <Message msg={{ ok: true, text: "Lozinka je promenjena." }} />
          )}
          <Button
            variant="secondary"
            onClick={() => {
              setDone(false);
              setOpen(true);
            }}
          >
            Promeni lozinku
          </Button>
        </div>
      </div>

      <Modal open={open} onClose={close} title="Promena lozinke">
        <form onSubmit={submit} className="flex max-w-sm flex-col gap-3">
          <Input label="Trenutna lozinka" type="password" value={form.current} onChange={set("current")} autoComplete="current-password" required />
          <Input label="Nova lozinka" type="password" value={form.next} onChange={set("next")} autoComplete="new-password" minLength={8} required />
          <Input label="Ponovi novu lozinku" type="password" value={form.repeat} onChange={set("repeat")} autoComplete="new-password" required />
          {err && (
            <p role="alert" className="flex items-center gap-1.5 text-sm text-danger-dark dark:text-danger-light">
              <IconWarning aria-hidden="true" /> {err}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" disabled={busy}>
              {busy ? "Čuvanje..." : "Promeni lozinku"}
            </Button>
            <Button type="button" variant="ghost" onClick={close}>
              Otkaži
            </Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
}

function ActiveFirmCard({ companies }) {
  // Only the active company from the session. The server returns only it anyway, and no list is shown here.
  const company = companies.find((f) => f.current) || companies[0];
  return (
    <Card title="Aktivna firma" icon={IconBuilding}>
      <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
        Firma sa kojom si trenutno prijavljen. Logo ide u zaglavlje izvoza (PDF, Excel) i štampe.
      </p>
      {company ? (
        <ul>
          <FirmItem company={company} />
        </ul>
      ) : (
        <p className="text-sm text-slate-500 dark:text-slate-400">Nema aktivne firme. Prijavi se ponovo.</p>
      )}
    </Card>
  );
}

/** Licence of the active company: expiry date and an Aktivna (green) / Neaktivna (red, expired) label. */
function LicenseCard({ company }) {
  if (!company) return null;
  const lic = licenseInfo(company);
  const active = lic.tone !== "expired";
  return (
    <Card title="Licenca" icon={IconShield}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className={`text-sm ${LICENSE_TONE_CLASS[lic.tone]}`}>
          Licenca ističe: <span className="font-medium tabular-nums">{lic.text}</span>
        </p>
        <span
          className={`inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold ${
            active
              ? "bg-success/10 text-success-dark dark:text-success-light"
              : "bg-danger/10 text-danger-dark dark:text-danger-light"
          }`}
        >
          <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${active ? "bg-success" : "bg-danger"}`} />
          {active ? "Aktivna" : "Neaktivna"}
        </span>
      </div>
    </Card>
  );
}

function FirmItem({ company }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const done = (text) => {
    invalidateBranding();
    notifyProfileChanged();
    if (text) setMsg({ ok: true, text });
  };

  const refresh = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await (await apiFetch(`/company_info_refresh.php?id=${company.id}`, { method: "POST" })).json();
      if (r.success) done("Podaci su preuzeti.");
      else setMsg({ ok: false, text: r.error || "Preuzimanje nije uspelo" });
    } catch {
      setMsg({ ok: false, text: "Server nije odgovorio." });
    }
    setBusy(false);
  };

  const sendLogo = async (dataUrl, err) => {
    if (!dataUrl) return setMsg({ ok: false, text: err || "Slika nije učitana" });
    setBusy(true);
    const r = await postJson(`/image_upload.php?type=company&id=${company.id}`, { data: dataUrl });
    setBusy(false);
    if (r.success) done("Logo je sačuvan.");
    else setMsg({ ok: false, text: r.error });
  };

  const removeLogo = async () => {
    setBusy(true);
    const r = await postJson(`/image_upload.php?type=company&id=${company.id}`, { remove: true });
    setBusy(false);
    if (r.success) done("Logo je uklonjen.");
    else setMsg({ ok: false, text: r.error });
  };

  const info = company.info;
  const rows = info ? FIELD_LABELS.filter(([k]) => info[k]) : [];

  return (
    <li className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
      <div className="flex flex-wrap items-start gap-3">
        {company.has_logo ? (
          <img
            src={imageUrl("company", company.id, company.logo_v)}
            alt={`Logo: ${company.label}`}
            className="h-14 w-24 shrink-0 rounded-md border border-slate-200 bg-white object-contain p-1 dark:border-slate-700"
          />
        ) : (
          <span aria-hidden="true" className="flex h-14 w-24 shrink-0 items-center justify-center rounded-md border border-dashed border-slate-300 text-slate-400 dark:border-slate-700">
            <IconImage className="text-xl" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="truncate text-sm font-semibold text-slate-900 dark:text-slate-50">{company.label}</h4>
          </div>
          <p className="truncate text-xs text-slate-500 dark:text-slate-400">Baza: {company.database_name}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" icon={IconRefresh} onClick={refresh} disabled={busy}>
          Preuzmi podatke firme
        </Button>
        <ImagePicker label={company.has_logo ? "Zameni logo" : "Dodaj logo"} maxSide={512} onPick={sendLogo} disabled={busy} icon={IconImage} />
        {company.has_logo && (
          <>
            <Button variant="ghost" size="sm" icon={IconTrash} onClick={removeLogo} disabled={busy}>
              Ukloni logo
            </Button>
            <a
              href={imageUrl("company", company.id, company.logo_v)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-primary hover:underline dark:text-primary-light"
            >
              <IconExternal aria-hidden="true" /> Otvori logo
            </a>
          </>
        )}
        <Message msg={msg} />
      </div>

      {info ? (
        <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 border-t border-slate-100 pt-3 text-sm dark:border-slate-800 sm:grid-cols-2">
          {rows.map(([k, label]) => (
            <div key={k} className="min-w-0">
              <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
              <dd className="break-words text-slate-900 dark:text-slate-50">{info[k]}</dd>
            </div>
          ))}
          {company.fetched_at && (
            <div className="text-xs text-slate-500 dark:text-slate-400 sm:col-span-2">Preuzeto: {company.fetched_at.slice(0, 16).replace("T", " ")}</div>
          )}
        </dl>
      ) : (
        <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
          Podaci firme još nisu preuzeti. Klikni "Preuzmi podatke firme".
        </p>
      )}
    </li>
  );
}
